/**
 * DASHBOARD-REPARTITION-CHART.WIDGET
 * Chart for the repartition
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useEffect, useState } from "react"
import { FilterState } from "@/redux/filter.types"
import { UserState } from "@/redux/user.types"
import { store } from "@/index"
import { fetchRepartition } from "@/redux/_archive.actions"
import { SurveyState } from "@/redux/survey.types"
import { Session } from "@/redux/_session.types"
import DashboardRepartitionBarsWidget from "./dashboard-repartition-bars.widget"
import DashboardRepartitionSkeletonWidget from "./skeletons/dashboard-repartition-skeleton.widget"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  survey: SurveyState
  user: UserState
}

interface OwnProps {
  questionNpsAid?: number
  isReport?: boolean
  //Pour le cas NPS, fourni par dashboard-nps.widget.tsx qui fetch la liste des questions
  //une seule fois pour toutes les cards — évite de refetch toute la liste ici, une fois
  //par question NPS affichée (cf session KB skeleton-loading-coordination)
  repartitionValues?: number[]
  onLoadingChange?: (isLoading: boolean) => void //Pour que le Card parent puisse pulser pendant le chargement
}

type Props = StateProps & OwnProps

function DashboardRepartitionChartWidget(props: Props) {
  const { t } = props

  //State
  //Values to display
  //Max value (for height)
  const [isLoading, setIsLoading] = useState(true)
  const [values, setValues] = useState<number[]>([])
  const [showData, setShowData] = useState(true)

  useEffect(() => {
    props.onLoadingChange?.(isLoading)
  }, [isLoading])

  //Load survey data on load or change filters
  //Set values and value max
  useEffect(() => {
    async function load() {
      const response: any = await store.dispatch(
        fetchRepartition(
          props.filter.dashboard,
          props._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction",
          null,
          null,
          undefined,
          undefined,
          props.isReport ?? false,
          props.survey.active.template.options.isGollac
        )
      )

      if (!response.error) {
        setValues(response.values)
        if (response.values.length) {
          setShowData(true)
        } else {
          setShowData(false)
        }
      } else {
        setShowData(false)
      }

      setIsLoading(false)
    }

    if (props.questionNpsAid) {
      //Le parent a déjà fetché la liste complète et extrait la repartition de cette
      //question — tant qu'il n'a pas fini, repartitionValues est undefined et on reste
      //en isLoading (pas de fetch ici, cf commentaire sur OwnProps.repartitionValues)
      if (props.repartitionValues) {
        setValues(props.repartitionValues)
        setIsLoading(false)
      }
    } else {
      load()
    }
  }, [
    props.survey.active.id,
    props.filter.dashboard,
    props.questionNpsAid,
    props._session.dashboardDisplaySettings.dashboardDisplayMode,
    props.survey.active.template.options.isGollac,
    props.isReport,
    props.repartitionValues
  ])

  return (
    <div className="repartition">
      {isLoading ? (
        <DashboardRepartitionSkeletonWidget
          showSummary={props.survey.active.template.options.isGollac && !props.questionNpsAid}
        />
      ) : showData ? (
        <DashboardRepartitionBarsWidget
          values={values.length > 1 ? values : []}
          questionNpsAid={props.questionNpsAid}
          displaySatisfaction={props._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction"}
          showCeils
        />
      ) : (
        <p className="grey-t">{t("survey_no_results")}</p>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  survey: state.survey,
  user: state.user
})

export default connect(mapStateToProps)(withTranslation()(DashboardRepartitionChartWidget))
